import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FadeInSection } from '../components/FadeInSection';

export function AboutPage() {
  useEffect(() => {
    document.title = 'About Dr Imraan Khan | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Dr Imraan Khan FCOG(SA) — specialist Obstetrician and Gynaecologist in Cape Town Southern Suburbs, Western Cape.'
      );
    }
  }, []);

  const expertise = [
    'Antenatal care — routine and high-risk',
    'Twin and multiple pregnancies',
    'Gestational diabetes management',
    'Pre-eclampsia monitoring and management',
    'Foetal growth restriction',
    'Vaginal birth and elective caesarean section',
    'Postpartum care',
    'General gynaecological consultations',
    'Abnormal uterine bleeding',
    'Polycystic ovary syndrome (PCOS)',
    'Endometriosis diagnosis and management',
    'Uterine fibroids and ovarian cysts',
    'Cervical screening (Pap smears) and colposcopy',
    'Contraception counselling and fitting (IUD, implant)',
    'Menopause management and HRT',
    'Diagnostic and operative laparoscopy',
    'Hysteroscopy',
    'Pelvic floor disorders',
  ];

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            Specialist Obstetrician &amp; Gynaecologist
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            About Dr Khan
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
        </div>
      </section>

      {/* ── Bio Section ── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-start">

            {/* Primary portrait — seated, blazer */}
            <FadeInSection className="flex-shrink-0 mx-auto md:mx-0">
              <div
                className="relative"
                style={{
                  borderRadius: '1.5rem',
                  padding: '4px',
                  background: 'linear-gradient(135deg, var(--color-gold) 0%, transparent 60%)',
                }}
              >
                <img
                  src="/images/about-seated.jpg?v=3"
                  alt="Dr Imraan Khan"
                  className="object-cover shadow-xl"
                  style={{ width: 320, height: 420, borderRadius: '1.4rem', objectFit: 'cover', objectPosition: 'center top', transform: 'rotate(-1deg)' }}
                />
              </div>
              <p
                className="text-center text-xs mt-3 font-body"
                style={{ color: 'var(--color-navy)', opacity: 0.5, fontFamily: 'var(--font-body)' }}
              >
                Dr Imraan Khan FCOG(SA)
              </p>
            </FadeInSection>

            {/* Bio text */}
            <FadeInSection delay={0.15} className="flex-1">
              <h2
                className="font-heading mb-4"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 400,
                  fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                  color: 'var(--color-navy)',
                }}
              >
                Dr Imraan Khan FCOG(SA)
              </h2>
              <p className="text-sm leading-relaxed mb-4 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                Dr Imraan Khan is a specialist Obstetrician and Gynaecologist based in Cape Town Southern Suburbs, Western Cape. He holds the Fellowship of the College of Obstetricians and Gynaecologists of South Africa (FCOG(SA)) after completing his specialist training.
              </p>
              <p className="text-sm leading-relaxed mb-4 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                With over a decade of experience in both obstetrics and gynaecology, Dr Khan is committed to providing personalised, evidence-based care to women at every stage of life — from adolescence through to menopause and beyond.
              </p>
              <p className="text-sm leading-relaxed mb-6 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                He has a special interest in high-risk obstetrics, laparoscopic surgery, and the initial assessment and referral of gynaecological cancers. Dr Khan consults at JKD at Melomed Gatesville and Melomed Mitchell's Plain.
              </p>

              {/* HPCSA */}
              <div
                className="rounded-lg p-4 inline-block"
                style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}
              >
                <p className="text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Registered with the HPCSA<br />
                  <span className="font-semibold" style={{ color: 'var(--color-primary)' }}>Registration No. MP0851140</span>
                </p>
              </div>

              <div className="mt-6">
                <p className="text-sm font-body mb-1" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
                  <strong>Consulting:</strong> JKD at Melomed Gatesville
                </p>
                <p className="text-sm font-body" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
                  <strong>Procedures:</strong> Melomed Mitchell's Plain
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── Secondary Photo — Theatre ── */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <img
              src="/images/surgical-portrait.jpg?v=4"
              alt="Dr Imraan Khan in theatre"
              className="w-full rounded-2xl shadow-lg object-cover"
              style={{ height: 420, objectPosition: 'center top' }}
            />
            <p
              className="text-center text-xs mt-3 font-body"
              style={{ color: 'var(--color-navy)', opacity: 0.5, fontFamily: 'var(--font-body)' }}
            >
              Dr Khan in theatre
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Areas of Expertise ── */}
      <section className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-10"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
              }}
            >
              Areas of Expertise
            </h2>
          </FadeInSection>
          <FadeInSection delay={0.1}>
            <div className="flex flex-wrap gap-3 justify-center">
              {expertise.map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 rounded-full text-sm font-body font-medium"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-primary)',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── Desk Photo ── */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInSection>
            <img
              src="/images/about-desk.jpg?v=4"
              alt="Dr Imraan Khan at his desk"
              className="w-full rounded-2xl shadow-lg object-cover"
              style={{ maxHeight: 500, objectPosition: 'center 40%' }}
            />
            <p
              className="text-xs mt-3 font-body opacity-50"
              style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
            >
              Dr Khan at his practice
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-16 px-4 text-center"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <FadeInSection>
          <h2
            className="font-heading text-white mb-3"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
            }}
          >
            Ready to book a consultation?
          </h2>
          <p className="text-white/70 mb-6 font-body" style={{ fontFamily: 'var(--font-body)' }}>
            Contact Dr Khan's practice to schedule your appointment.
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity font-body"
            style={{
              backgroundColor: 'var(--color-gold)',
              color: 'var(--color-navy)',
              fontFamily: 'var(--font-body)',
            }}
          >
            Book an Appointment →
          </Link>
        </FadeInSection>
      </section>
    </div>
  );
}
